import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from './Navbar.module.css';

export default function Navbar() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { currentUser, logout, isAdmin } = useAuth();
  const [error, setError] = useState('');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuEmail = currentUser?.email || '';

  const toggleAccountMenu = () => {
    setMobileMenuOpen(false);
    setAccountMenuOpen(prev => !prev);
  };

  const toggleMobileMenu = () => {
    setAccountMenuOpen(false);
    setMobileMenuOpen(prev => !prev);
  };

  useEffect(() => {
    setMobileMenuOpen(false);
    setAccountMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(`.${styles.accountMenuWrapper}`)) return;
      setAccountMenuOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAccountMenuOpen(false);
      }
    };

    if (accountMenuOpen) {
      document.addEventListener('mousedown', handlePointerDown);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [accountMenuOpen, styles.accountMenuWrapper]);

  async function handleLogout() {
    setError('');
    try {
      await logout();
      // Close mobile menu if open
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
      setAccountMenuOpen(false);
    } catch {
      setError('Failed to log out');
    }
  }

  const avatarText = (accountMenuEmail || 'A').slice(0, 1).toUpperCase();

  return (
    <nav className={styles.navbar}>
      <div className={styles.navContent}>
        <Link to="/" className={styles.logoLink}>
          <span className={styles.logo}>MPD</span>
        </Link>

        <div className={styles.rightControls}>
          <div className={`${styles.navLinks} ${mobileMenuOpen ? styles.mobileMenuOpen : ''}`}>
          <Link
            to="/"
            className={`${styles.navLink} ${location.pathname === '/' ? styles.active : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            Home
          </Link>
          <Link
            to="/id"
            className={`${styles.navLink} ${location.pathname === '/id' ? styles.active : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            ID
          </Link>
          <Link
            to="/vision"
            className={`${styles.navLink} ${location.pathname === '/vision' ? styles.active : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            Vision
          </Link>
          <Link
            to="/for-networkers"
            className={`${styles.navLink} ${location.pathname === '/for-networkers' ? styles.active : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            For Networkers
          </Link>
          <Link
            to="/the-hype"
            className={`${styles.navLink} ${location.pathname === '/the-hype' ? styles.active : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            The Hype
          </Link>
          <Link
            to="/buy"
            className={`${styles.navLink} ${location.pathname === '/buy' ? styles.active : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            Buy Now
          </Link>
          <Link
            to="/affiliate-program"
            className={`${styles.navLink} ${location.pathname === '/affiliate-program' ? styles.active : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            Affiliate Program
          </Link>

          {/* Mobile panel auth actions (avatar stays in top bar on mobile) */}
          {currentUser ? (
            <div className={styles.mobileAuthLinks}>
              {isAdmin && (
                <>
                  <Link
                    to="/admin"
                    className={`${styles.navLink} ${styles.dashboardLink} ${location.pathname === '/admin' ? styles.active : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin
                  </Link>
                  <Link
                    to="/user-profile-dashboard"
                    className={`${styles.navLink} ${styles.dashboardLink} ${location.pathname === '/user-profile-dashboard' ? styles.active : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    User Profiles
                  </Link>
                </>
              )}
              <Link
                to="/dashboard"
                className={styles.navLink}
                onClick={() => setMobileMenuOpen(false)}
              >
                Dashboard
              </Link>
              <Link
                to="/user-profile"
                className={styles.navLink}
                onClick={() => setMobileMenuOpen(false)}
              >
                Profile
              </Link>
              <button type="button" className={styles.logoutButton} onClick={handleLogout}>
                Logout
              </button>
            </div>
          ) : (
            <div className={styles.mobileAuthLinks}>
              <Link
                to="/login"
                className={`${styles.navLink} ${styles.loginLink} ${location.pathname === '/login' ? styles.active : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Link>
              <Link
                to="/signup"
                className={`${styles.navLink} ${styles.signupLink} ${location.pathname === '/signup' ? styles.active : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign Up
              </Link>
            </div>
          )}
        </div>

          <div className={styles.topControls}>
            {currentUser ? (
              <div className={styles.accountMenuWrapper}>
                <button
                  type="button"
                  className={styles.avatarButton}
                  onClick={toggleAccountMenu}
                  aria-haspopup="menu"
                  aria-expanded={accountMenuOpen}
                  aria-label="Account menu"
                  title={accountMenuEmail ? `Account: ${accountMenuEmail}` : 'Account'}
                >
                  <span className={styles.avatarCircle} aria-hidden="true">
                    {avatarText}
                  </span>
                </button>

                {accountMenuOpen && (
                  <div className={styles.accountMenu} role="menu">
                    <div className={styles.accountMenuHeader}>
                      <div className={styles.accountMenuHeaderLabel}>Account:</div>
                      <div className={styles.accountMenuHeaderEmail}>{accountMenuEmail || '-'}</div>
                    </div>

                    <Link
                      to="/dashboard"
                      role="menuitem"
                      className={styles.accountMenuItem}
                      onClick={() => {
                        setAccountMenuOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      Dashboard
                    </Link>

                    <Link
                      to="/user-profile"
                      role="menuitem"
                      className={styles.accountMenuItem}
                      onClick={() => {
                        setAccountMenuOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      Profile
                    </Link>

                    <button
                      type="button"
                      role="menuitem"
                      className={styles.accountMenuItem}
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.authLinks}>
                <Link
                  to="/login"
                  className={`${styles.navLink} ${styles.loginLink} ${location.pathname === '/login' ? styles.active : ''}`}
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className={`${styles.navLink} ${styles.signupLink} ${location.pathname === '/signup' ? styles.active : ''}`}
                >
                  Sign Up
                </Link>
              </div>
            )}

            <button
              type="button"
              className={styles.mobileMenuButton}
              onClick={toggleMobileMenu}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              <span className={`${styles.hamburgerLine} ${mobileMenuOpen ? styles.open : ''}`}></span>
              <span className={`${styles.hamburgerLine} ${mobileMenuOpen ? styles.open : ''}`}></span>
              <span className={`${styles.hamburgerLine} ${mobileMenuOpen ? styles.open : ''}`}></span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
